/*
 * Preserve incoming attribution context across same-site conversion handoffs.
 *
 * Shopify, variant renders, cart checkout forms, and static Liquid links do
 * not automatically carry landing query params into later quote, cart, or
 * label-designer URLs. Keep the original external campaign/click identifiers
 * in sessionStorage and append them only to high-intent internal destinations
 * when they are absent.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cw_attribution_params_v1';
  var EXPLICIT_PARAMS = {
    gclid: true,
    gbraid: true,
    wbraid: true,
    gad_source: true,
    gad_campaignid: true,
    msclkid: true,
    fbclid: true,
    ttclid: true,
    twclid: true,
    li_fat_id: true,
    irclickid: true,
    srsltid: true
  };
  var PRESERVED_PATHS = [
    '/tools/label-designer',
    '/pages/quote',
    '/cart',
    '/checkout',
    '/products/'
  ];

  function isAttributionParam(name) {
    var normalized = (name || '').toLowerCase();
    return normalized.indexOf('utm_') === 0 || EXPLICIT_PARAMS[normalized] === true;
  }

  function readQueryAttribution() {
    var params = {};
    if (!window.location.search || window.location.search.length < 2) return params;

    var searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach(function (value, name) {
      if (!isAttributionParam(name) || value === '') return;
      params[name] = value;
    });

    return params;
  }

  function loadStoredAttribution() {
    try {
      var stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return {};
      var parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function storeAttribution(params) {
    if (!Object.keys(params).length) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    } catch (e) {
      // Storage can be blocked; link decoration still works for the current URL.
    }
  }

  function getAttribution() {
    var stored = loadStoredAttribution();
    var current = readQueryAttribution();
    var merged = Object.assign({}, stored, current);
    storeAttribution(merged);
    return merged;
  }

  function shouldPreserveForPath(pathname) {
    return PRESERVED_PATHS.some(function (prefix) {
      return pathname === prefix || pathname.indexOf(prefix) === 0;
    });
  }

  function buildSameOriginHref(url) {
    return url.pathname + url.search + url.hash;
  }

  function isAnchorElement(target) {
    return typeof HTMLAnchorElement !== 'undefined' && target instanceof HTMLAnchorElement;
  }

  function isFormElement(target) {
    return typeof HTMLFormElement !== 'undefined' && target instanceof HTMLFormElement;
  }

  function getUrlAttributeName(target) {
    if (isAnchorElement(target)) return 'href';
    if (isFormElement(target)) return 'action';
    return '';
  }

  function decorateTarget(target, attribution) {
    if (!target || target.dataset.preserveAttribution === 'false') return;
    if (!attribution || !Object.keys(attribution).length) return;

    var attributeName = getUrlAttributeName(target);
    if (!attributeName) return;

    var rawUrl = target.getAttribute(attributeName);
    if (!rawUrl || rawUrl.charAt(0) === '#') return;

    var url;
    try {
      url = new URL(rawUrl, window.location.href);
    } catch (e) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!shouldPreserveForPath(url.pathname)) return;

    var changed = false;
    Object.keys(attribution).forEach(function (name) {
      if (url.searchParams.has(name)) return;
      url.searchParams.set(name, attribution[name]);
      changed = true;
    });

    if (changed) {
      target.setAttribute(attributeName, buildSameOriginHref(url));
    }
  }

  function decorateTargets(root) {
    var attribution = getAttribution();
    if (!Object.keys(attribution).length) return;

    if (isAnchorElement(root) || isFormElement(root)) {
      decorateTarget(root, attribution);
    }

    if (!root.querySelectorAll) return;
    root.querySelectorAll('a[href], form[action]').forEach(function (target) {
      decorateTarget(target, attribution);
    });
  }

  function decorateEventTarget(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var urlTarget = target.closest('a[href], form[action]');
    if (urlTarget) decorateTarget(urlTarget, getAttribution());
  }

  getAttribution();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      decorateTargets(document);
    }, { once: true });
  } else {
    decorateTargets(document);
  }

  ['pointerdown', 'touchstart', 'focusin', 'click', 'submit'].forEach(function (eventName) {
    document.addEventListener(eventName, decorateEventTarget, true);
  });

  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) decorateTargets(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
